import { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import firebase from '../../firebase/compat'
import { v4 as uuid } from 'uuid'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'

import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { useFirestoreOrderBy } from '../../firebase/useFirestore'
import { Auth } from '../../StateManagment/Auth'
import { SavedIcon } from '../../StateManagment/SavedIcon'

import Table from '../../components/common/table/Table'
import TableCell from '../../components/common/table/TableCell'
import TableTitleCell from '../../components/common/table/TableTitleCell'
import DeleteModal from '../../components/common/DeleteModal'
import Tooltip from '../../components/common/Tooltip'
import PageHeader from '../../components/layout/PageHeader'
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard'
import WervingsactieModal from './WervingsactieModal'

const STATUS_LABELS = { active: 'Actief', done: 'Afgerond' }

// Eén bouwer voor de lijst én voor het openen na aanmaken. Detail.jsx leest
// het id op positie 3 van deze url.
// AI-CONTEXT: See docs/pitfalls.md#routing-url-built-here-read-by-position-there
/**
 * @param {string} id - ID van de wervingsactie
 * @returns {string}
 */
const actieLink = (id) => `/${client}/wervingsactie/${id}`

const Overview = () => {
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)
    const history = useHistory()

    const [editorOpen, setEditorOpen] = useState(false)
    const [selectedActie, setSelectedActie] = useState(null)
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('')
    const [deleteName, setDeleteName] = useState('')

    const wervingsacties = useFirestoreOrderBy('Wervingsacties', 'Position', 'asc')
    // Soft-deleted records (ADR-008) stay in Firestore — exclude them from the list.
    const items = (Array.isArray(wervingsacties) ? wervingsacties : []).filter((a) => !a.Deleted)

    const openAdd = () => {
        setSelectedActie(null)
        setEditorOpen(true)
    }

    const openEdit = (actie) => {
        setSelectedActie(actie)
        setEditorOpen(true)
    }

    const saveActie = async (form) => {
        let nieuwId = null

        if (selectedActie) {
            await db.collection('Wervingsacties').doc(selectedActie.docid).update({
                Title: form.Title,
                StartDate: form.StartDate,
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            })
        } else {
            nieuwId = uuid()
            await db.collection('Wervingsacties').add({
                ID: nieuwId,
                CompagnyID: client,
                Compagny: client,
                Position: items.length + 1,
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                Title: form.Title,
                StartDate: form.StartDate,
                Status: 'active',
                Gemeenten: [],
                Wijken: [],
                Deleted: false,
            })
        }

        setSaved('flex')
        setEditorOpen(false)
        setSelectedActie(null)

        // Het gebied kies je op de actie zelf; zonder deze stap bleef je op de
        // lijst staan en raakte een nieuwe actie nooit gevuld.
        if (nieuwId) history.push(actieLink(nieuwId))
    }

    const statusHandler = async (e) => {
        const docid = e.target.dataset.docid
        const value = e.target.value
        await db.collection('Wervingsacties').doc(docid).update({ Status: value })
        setSaved('flex')
    }

    const deleteModalHandler = (e) => {
        e.stopPropagation()
        setDeleteDocid(e.currentTarget.dataset.docid)
        setDeleteName(e.currentTarget.dataset.deletename)
        setDeleteModal(true)
    }

    // Soft delete (ADR-008): flag the doc instead of removing it.
    const deleteActie = async () => {
        await db.collection('Wervingsacties').doc(deleteDocid).update({
            Deleted: true,
            DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
            DeletedBy: auth.ID,
        })
        setSaved('flex')
    }

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader
                    heading={'Wervingsacties'}
                    description={'Het overzicht van alle wervingsacties (deuracties)'}
                    AI={false}
                />
                <div className='table-container'>
                    <ReadOnlyGuard edit='housevisits.edit'>
                        {(canEdit) => (
                            <Table
                                columns={[
                                    { key: 'title', label: 'WERVINGSACTIE' },
                                    { key: 'startdate', label: 'STARTDATUM', searchable: false },
                                    { key: 'status', label: 'STATUS', searchable: false },
                                    { key: 'edit', label: 'BEWERK', searchable: false },
                                    { key: 'delete', label: 'VERWIJDER', searchable: false },
                                ]}
                                items={items}
                                getItemKey={(a) => a.ID}
                                renderRow={(a) => (
                                    <>
                                        <TableTitleCell
                                            value={a.Title || '—'}
                                            to={actieLink(a.ID)}
                                            readOnly
                                        />
                                        <TableCell>
                                            <p>{a.StartDate || '—'}</p>
                                        </TableCell>
                                        <TableCell>
                                            {canEdit ? (
                                                <select data-docid={a.docid} value={a.Status || 'active'} onChange={statusHandler}>
                                                    <option value='active'>Actief</option>
                                                    <option value='done'>Afgerond</option>
                                                </select>
                                            ) : (
                                                <p>{STATUS_LABELS[a.Status] || 'Actief'}</p>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {canEdit && (
                                                <Tooltip content={'Wervingsactie bewerken'} top='-60px' width={'30px'}>
                                                    <EditOutlinedIcon className='table-delete-icon' onClick={() => openEdit(a)} />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {canEdit && (
                                                <Tooltip content={'Wervingsactie verwijderen'} top='-60px' width={'30px'}>
                                                    <DeleteOutlinedIcon
                                                        className='table-delete-icon'
                                                        data-docid={a.docid}
                                                        data-deletename={a.Title || 'deze wervingsactie'}
                                                        onClick={deleteModalHandler}
                                                    />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </>
                                )}
                                searchAccessor={(a) => a?.Title || ''}
                                searchPlaceholder='Zoeken op wervingsactie'
                                emptyMessage='Nog geen wervingsacties toegevoegd.'
                                addRow={canEdit ? {
                                    toolTipContent: 'Wervingsactie toevoegen',
                                    onClick: openAdd,
                                } : undefined}
                            />
                        )}
                    </ReadOnlyGuard>
                </div>
            </div>

            <WervingsactieModal
                modalOpen={editorOpen}
                setModalOpen={setEditorOpen}
                actie={selectedActie}
                onSave={saveActie}
            />

            <DeleteModal
                deleteModal={deleteModal}
                setDeleteModal={setDeleteModal}
                deleteItem={deleteActie}
                deleteName={deleteName}
                softDelete
              />
        </div>
    )
}

export default Overview
